/**
 * Phase 2A — Create Care Plan dialog (participant-facing).
 *
 * Three flows, all producing a DRAFT plan:
 *   1. From template — active, scope-valid templates for this participant.
 *   2. Create blank — empty plan.
 *   3. Duplicate existing plan — clone one of this participant's plans.
 *
 * Only rendered when `participant_careplan_template_management_enabled` is ON.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { FileText, FilePlus2, Copy, Eye, Loader2, Search } from 'lucide-react';

// BS3-012: every new plan starts as a DRAFT; staff only see approved plans
export const STAFF_VISIBILITY_NOTE = 'Saved as a draft — staff will see it once it is submitted for review and approved.';

const CATEGORIES = ['SIL', 'SDA', 'PAEDIATRIC', 'RESPITE', 'CENTRE_BASED', 'COMMUNITY', 'GENERIC', 'CUSTOM'];

export function CreateCarePlanDialog({ open, onOpenChange, participantId, existingPlans = [], onCreated }) {
  const [tab, setTab] = useState('template');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('ALL');
  const [preview, setPreview] = useState(null);
  const [blankTitle, setBlankTitle] = useState('');

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/careplan-templates?for_participant_id=${participantId}`);
      setTemplates(r.data.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [participantId]);

  useEffect(() => {
    if (open) {
      setTab('template'); setPreview(null); setBlankTitle(''); setSearch(''); setCategory('ALL');
      loadTemplates();
    }
  }, [open, loadTemplates]);

  const filtered = templates.filter(t =>
    (category === 'ALL' || t.category === category) &&
    (!search || (t.name || '').toLowerCase().includes(search.toLowerCase()))
  );

  const openPreview = async (t) => {
    try {
      const r = await api.get(`/careplan-templates/${t.id}/preview`);
      setPreview(r.data.data);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const createFromTemplate = async (t) => {
    setBusy(true);
    try {
      const r = await api.post(`/careplans/from-managed-template/${t.id}`, { participant_id: participantId });
      toast.success(`Care plan created from "${t.name}"`, { description: STAFF_VISIBILITY_NOTE });   // BS3-012
      onOpenChange(false);
      onCreated?.(r.data.data);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const createBlank = async () => {
    setBusy(true);
    try {
      const r = await api.post('/careplans', { participant_id: participantId, title: blankTitle || 'New Care Plan' });
      toast.success('Blank care plan created', { description: STAFF_VISIBILITY_NOTE });   // BS3-012
      onOpenChange(false);
      onCreated?.(r.data.data);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const duplicatePlan = async (p) => {
    setBusy(true);
    try {
      const r = await api.post(`/careplans/${p.id}/duplicate`);
      toast.success('Care plan duplicated', { description: STAFF_VISIBILITY_NOTE });   // BS3-012
      onOpenChange(false);
      onCreated?.(r.data.data);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-care-plan-dialog">
        <DialogHeader>
          <DialogTitle>Create Care Plan</DialogTitle>
          <DialogDescription>Start from a template, a blank plan, or duplicate an existing plan.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="template" data-testid="ccp-tab-template"><FileText className="h-3 w-3 mr-1" /> From template</TabsTrigger>
            <TabsTrigger value="blank" data-testid="ccp-tab-blank"><FilePlus2 className="h-3 w-3 mr-1" /> Blank</TabsTrigger>
            <TabsTrigger value="duplicate" data-testid="ccp-tab-duplicate"><Copy className="h-3 w-3 mr-1" /> Duplicate</TabsTrigger>
          </TabsList>

          {/* ── From template ── */}
          <TabsContent value="template" className="space-y-3 mt-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input placeholder="Search templates" value={search} onChange={e => setSearch(e.target.value)}
                       className="pl-7" data-testid="ccp-template-search" />
              </div>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-40" data-testid="ccp-template-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All categories</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading templates…</div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-8 text-center" data-testid="ccp-template-empty">
                No active templates available for this participant's branch. Try "Blank" or ask an admin to publish a template.
              </p>
            ) : (
              <ScrollArea className="h-72">
                <ul className="space-y-2 pr-3">
                  {filtered.map(t => (
                    <li key={t.id} className="border rounded-lg px-3 py-2 flex items-center justify-between hover:bg-slate-50"
                        data-testid={`ccp-template-${t.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm truncate">{t.name}</p>
                          <Badge variant="outline" className="text-[10px]">v{t.version}</Badge>
                          <Badge variant="secondary" className="text-[10px]">{t.scope_level}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {t.category} · review {t.review_cycle_months}mo{t.description ? ` · ${t.description}` : ''}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => openPreview(t)} data-testid={`ccp-preview-${t.id}`}>
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button size="sm" disabled={busy} onClick={() => createFromTemplate(t)} data-testid={`ccp-use-${t.id}`}>
                          Use
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            )}

            {preview && (
              <div className="border rounded-lg p-3 bg-slate-50" data-testid="ccp-preview-panel">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-sm">{preview.name} · v{preview.version}</p>
                  <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>Close preview</Button>
                </div>
                <ScrollArea className="h-40">
                  <ul className="text-xs space-y-1 pr-2">
                    {(preview.sections_structured || []).map((s, i) => (
                      <li key={i} className="flex justify-between"><span>{s.title || s.section_key}</span><span className="text-muted-foreground">{(s.rows || []).length} rows</span></li>
                    ))}
                    {(preview.sections_simple || []).map((s, i) => (
                      <li key={`s${i}`}><span className="font-medium">{s.label || s.key}</span>: <span className="text-muted-foreground">{(s.content || '').slice(0, 80) || '—'}</span></li>
                    ))}
                    {(preview.sections_structured || []).length === 0 && (preview.sections_simple || []).length === 0 && (
                      <li className="italic text-muted-foreground">No sections defined.</li>
                    )}
                  </ul>
                </ScrollArea>
              </div>
            )}
          </TabsContent>

          {/* ── Blank ── */}
          <TabsContent value="blank" className="space-y-3 mt-3">
            <div className="space-y-2">
              <Label htmlFor="ccp-blank-title">Plan title</Label>
              <Input id="ccp-blank-title" placeholder="e.g. Support Plan 2026" value={blankTitle}
                     onChange={e => setBlankTitle(e.target.value)} data-testid="ccp-blank-title" />
            </div>
            <div className="flex justify-end">
              <Button disabled={busy} onClick={createBlank} data-testid="ccp-create-blank">
                {busy && <Loader2 className="h-3 w-3 animate-spin mr-1" />} Create blank plan
              </Button>
            </div>
          </TabsContent>

          {/* ── Duplicate ── */}
          <TabsContent value="duplicate" className="space-y-3 mt-3">
            {existingPlans.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-8 text-center" data-testid="ccp-duplicate-empty">
                No existing plans to duplicate.
              </p>
            ) : (
              <ScrollArea className="h-72">
                <ul className="space-y-2 pr-3">
                  {existingPlans.map(p => (
                    <li key={p.id} className="border rounded-lg px-3 py-2 flex items-center justify-between hover:bg-slate-50"
                        data-testid={`ccp-dup-plan-${p.id}`}>
                      <div>
                        <p className="font-medium text-sm">{p.title || p.plan_no}</p>
                        <p className="text-xs text-muted-foreground">{p.plan_no} · v{p.version} · {p.status}</p>
                      </div>
                      <Button size="sm" disabled={busy} onClick={() => duplicatePlan(p)} data-testid={`ccp-dup-${p.id}`}>
                        Duplicate
                      </Button>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export default CreateCarePlanDialog;
